import { useLayoutEffect, useState } from 'react';
import type { DependencyList, RefObject } from 'react';

export interface FitToWidthResult {
  /** The largest candidate size (within `[min, max]`) whose probe width fit inside the
   *  container, or `min` (the floor) if nothing fit. */
  size: number;
  /** False when nothing fit even at the floor — `size` is the floor and the caller should
   *  apply its own last-resort overflow handling (wrap, ellipsis, etc). */
  fits: boolean;
}

export interface UseFitToWidthOptions {
  min: number;
  max: number;
  step: number;
  /** Applies a candidate size to the probe DOM node(s) — e.g. set a single span's fontSize,
   *  or a per-pill name font + ratio-scaled label font. Called once per candidate in the
   *  descending fit loop, immediately followed by `measureProbe` (batched write → single read,
   *  forcing one synchronous layout per candidate instead of thrashing). */
  applyToProbe: (size: number) => void;
  /** Reads the probe's current (post-`applyToProbe`) intrinsic width for the active candidate. */
  measureProbe: () => number;
  /** Re-runs the fit loop when any value here changes — mirrors a useLayoutEffect dependency
   *  array (e.g. text content, or a joined content key like a pills `key:value` string). */
  deps: DependencyList;
}

/**
 * Shared "shrink to fit before paint" algorithm.
 *
 * Owns: containerRef/probeRef wiring, the ResizeObserver lifecycle (observe container,
 * disconnect on cleanup), the useLayoutEffect, and the descending-candidate loop that applies
 * each candidate size and reads the probe's width once per candidate, stopping at the first
 * size that fits.
 *
 * Callers own: what "the probe" looks like (a single hidden text span vs. a multi-pill hidden
 * row) and how a candidate size is applied to it — passed in via `applyToProbe`/`measureProbe`.
 * The probe itself must stay out of layout flow (visibility:hidden + position:absolute, NOT
 * display:none — display:none nodes report 0 for scrollWidth) so it doesn't perturb the
 * container being measured.
 *
 * Perf: the observed container typically has a fixed width but fit-driven (intrinsic) height,
 * so the ResizeObserver fires again once the post-fit height settles even though width didn't
 * change. This hook tracks the last observed width *within a single effect run* and bails out
 * when it repeats, avoiding a redundant identical fit pass. The tracker is scoped to the effect
 * body (not a persistent ref) so every `deps` change gets a fresh fit even when the container
 * width is unchanged (e.g. a new player name / a new deck's pills at the same panel width).
 */
export function useFitToWidth(
  containerRef: RefObject<HTMLElement | null>,
  probeRef: RefObject<HTMLElement | null>,
  { min, max, step, applyToProbe, measureProbe, deps }: UseFitToWidthOptions
): FitToWidthResult {
  const [result, setResult] = useState<FitToWidthResult>({ size: max, fits: true });

  useLayoutEffect(() => {
    const container = containerRef.current;
    const probe = probeRef.current;
    if (!container || !probe) return;

    // Local to this effect run — resets on every deps change so content-only changes always
    // re-fit, while the redundant post-fit height-only resize within one run is still skipped.
    let lastWidth: number | null = null;

    const fit = (width: number) => {
      // Skip if the container hasn't been laid out yet, or the width is unchanged since the
      // last fit in this run (see perf note above — avoids re-fitting on the height-only resize).
      if (width === 0 || width === lastWidth) return;
      lastWidth = width;

      let fittedSize = min;
      let found = false;

      for (let size = max; size >= min; size -= step) {
        applyToProbe(size);
        if (measureProbe() <= width) {
          fittedSize = size;
          found = true;
          break;
        }
      }

      // Nothing fit even at the floor: report the floor with fits=false so the caller applies
      // its own last-resort overflow handling instead of this hook silently picking one.
      setResult({ size: fittedSize, fits: found });
    };

    fit(container.clientWidth);

    // Re-fit when the container width changes (panel compact↔full toggle, window resize, etc.).
    // Read clientWidth (border-box inner width, includes padding) in both paths so the "available
    // width" is consistent and padding-safe for any future padded caller — not contentRect.width.
    const ro = new ResizeObserver(() => {
      fit(container.clientWidth);
    });
    ro.observe(container);
    return () => ro.disconnect();
    // deps is caller-supplied and mirrors each call site's own dependency list
    // (content/text/pillsKey); applyToProbe/measureProbe read live refs at
    // call time so they never go stale between deps changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return result;
}
